import React, { useState } from 'react';
import DataTable from 'react-data-table-component';
import { IoIosArrowDown } from "react-icons/io";
import { IoIosArrowUp } from "react-icons/io";

const AnomaliesTable = ({ data, selectedTags, onRowSelect }) => {

    const [expandedRowKey, setExpandedRowKey] = useState(null);

    // Row click only highlights
    const handleRowClick = (row) => {
        if (onRowSelect) {
            onRowSelect(row);
        }
    };

    // Button click toggles dropdown
    const handleExpandClick = (row, e) => {
        e.stopPropagation(); // Prevent row click event
    const key = row.DEP_TAG_ALIAS + '_' + row.TIMESTAMP;
    setExpandedRowKey(expandedRowKey === key ? null : key);
    };

    const columns = [
        {
            name: 'Tag',
            selector: row => row.DEP_TAG_ALIAS,
            sortable: true,
            cell: row => (
                <span title={row.DEP_TAG_ALIAS} style={{whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis',maxWidth:'120px'}}>{row.DEP_TAG_ALIAS}</span>
            )
        },
        {
            name: 'Timestamp',
            selector: row => row.TIMESTAMP,
            sortable: true,
            cell: row => <div title={row.TIMESTAMP} style={{whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis',maxWidth:'140px'}}>{row.TIMESTAMP}</div>
        },
        {
            name: 'Value',
            selector: row => row.SNSR_VALUE,
            sortable: true,
            cell: row => <div title={row.SNSR_VALUE} style={{whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis',maxWidth:'80px'}}>{row.SNSR_VALUE}</div>
        },
        {
            name: 'Anomaly',
            selector: row => row.FLG_ANOMALIES,
            sortable: true,
            cell: row => <div title={row.FLG_ANOMALIES} style={{whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis',maxWidth:'80px'}}>{row.FLG_ANOMALIES}</div>
        },
         {
            name: '',
            button: true,
            width: '56px',
            cell: row => {
                const key = row.DEP_TAG_ALIAS + '_' + row.TIMESTAMP;
                return (
                    <button
                        onClick={(e) => handleExpandClick(row, e)}
                        aria-label={expandedRowKey === key ? 'Collapse details' : 'Expand details'}
                    >{expandedRowKey === key ? <IoIosArrowUp/> : <IoIosArrowDown/>}</button>
                );
            }
        },
    ];

    const customStyles = {
        headCells: {
            style: {
                fontSize: '13px',
                backgroundColor: '#e3e1e1',
            },
        },
        cells: {
            style: {
                fontSize: '13.4px',
            },
        },
        table: {
            style: {
                border: '1px solid #e3e1e1',
                margin: '0 auto',
            },
        },
    };

    const conditionalRowStyles = [
        {
            when: row => (row.DEP_TAG_ALIAS + '_' + row.TIMESTAMP) === expandedRowKey,
            style: {
                borderBottom: 'none',
                backgroundColor: '#ededed',
            },
        },
    ];

    // Convert object format to array of anomaly rows
const getAnomalyRows = (data) => {
    if (
        !data ||
        !data.tag_aliases ||
        !data.timestamps ||
        !data.values ||
        !data.anomalies
    ) return [];

    const rows = [];
    data.timestamps.forEach((timestamp, tIdx) => {
        data.tag_aliases.forEach((tag) => {
            // Handle both string "1" and number 1 for anomaly flags
            const anomalyFlag = data.anomalies[tag][tIdx];
            const isAnomaly = anomalyFlag === 1 || anomalyFlag === "1";
            if (isAnomaly) {
                rows.push({
                    DEP_TAG_ALIAS: tag,
                    TIMESTAMP: timestamp,
                    SNSR_VALUE: data.values[tag][tIdx],
                    FLG_ANOMALIES: data.anomalies[tag][tIdx],
                    HIGH_LIMIT: data.high_limits ? data.high_limits[tag][tIdx]  : undefined,
                    LOW_LIMIT: data.low_limits ? data.low_limits[tag][tIdx] : undefined,
                    INFERENCE_JOB_NAME: data.inference_job_name || "",
                    FORECAST_JOB_NAME: data.forecast_job_name || "",
                    CREATED_AT: data.created_at || "",
                });
            }
        });
    });
    return rows;
};

const anomalyRows = getAnomalyRows(data);

// Filter data based on selected tags
const filteredData =
    selectedTags && selectedTags.length > 0
        ? anomalyRows.filter(item =>
            selectedTags.map(e => e.value || e.DEP_TAG_ALIAS).includes(item.DEP_TAG_ALIAS)
        )
        : anomalyRows;

    return (
        <>
            <div className='w-[65%] '>
                <p className='font-semibold text-2xl text-btn-primary w-[65%] mb-5'>{`Anomalies Detected (${filteredData.length})`}</p>
                <DataTable
                    columns={columns}
                    data={filteredData}
                    paginationTotalRows={filteredData.length}
                    pagination
                    paginationPerPage={5}
                    paginationRowsPerPageOptions={[5, 10, 15, 20]}
                    highlightOnHover
                    responsive
                    customStyles={customStyles}
                    expandableRows
                    expandableRowsHideExpander
                    expandableRowExpanded={row => (row.DEP_TAG_ALIAS + '_' + row.TIMESTAMP) === expandedRowKey}
                    onRowClicked={handleRowClick}
                    expandableRowsComponent={ExpandedComponent}
                    conditionalRowStyles={conditionalRowStyles}
                />
            </div>
        </>
    );
};

export default AnomaliesTable;

const ExpandedComponent = ({ data }) => (
    <div style={{ padding: '10px 20px', fontSize: '13px', backgroundColor: '#f9f9f9', display: 'flex', gap: '50px' }}>
        <div>
            <p className='mb-1'><strong>Created Date : </strong> {data.CREATED_AT}</p>
            <p className='mb-1 '><strong>DGS Tags:</strong> {data.DEP_TAG_ALIAS}</p>
            <p className='mb-1'><strong>High Limit:</strong> {data.HIGH_LIMIT ?? '-'}</p>
            <p className='mb-1'><strong>Low Limit:</strong> {data.LOW_LIMIT ?? '-'}</p>
            <p className='mb-1'><strong>Sensor Value :</strong> {data.SNSR_VALUE}</p>
            {data.FORECAST_JOB_NAME ? (
                <p className='mb-1'><strong>Forecast Job Name :</strong> {data.FORECAST_JOB_NAME}</p>
            ) : (
                <p className='mb-1'><strong>Inference Job Name :</strong> {data.INFERENCE_JOB_NAME}</p>
            )}
            <p className='mb-1'><strong>Timestamp :</strong> {data.TIMESTAMP}</p>
        </div>
    </div>
);
